/**
 * Where a sign-in requirement is fixed, as an address (docs/PLAN-signin-requirements-20261029.md): two-step sign-in is
 * set up in Settings → "how you sign in" (its card lands on `#two-step`), and an email is confirmed on the finish-setup
 * screen, which resends the link and takes "I've confirmed it". The countdown's "set it up" in the banner band and the
 * bell's fix row for a requirement notice (metadata.kind 'signin_requirement') both lead here, so the two never send a
 * person to different places for the same thing.
 *
 * Imports nothing but types: the banner band and the bell are on the first download.
 */
import type { RequirementKind } from '../../types/signInSecurityVocabulary';

/** The two-step sign-in section's id on Settings, which an arrival at SET_UP_TWO_STEP_TO lands on. */
export const TWO_STEP_ANCHOR = 'two-step';

/** Settings, at its two-step sign-in card. */
export const SET_UP_TWO_STEP_TO = `/settings#${TWO_STEP_ANCHOR}`;

/** The finish-setup screen, where the sign-in email is confirmed. */
export const CONFIRM_EMAIL_TO = '/sign-in-setup';

/** Where each kind of requirement is fixed (typed over the vocabulary: a new kind fails to compile until it has a place). */
const FIX_PATHS: Record<RequirementKind, string> = {
  'two-step': SET_UP_TWO_STEP_TO,
  'email-confirmed': CONFIRM_EMAIL_TO,
};

/** Where a requirement of this kind is fixed. */
export function requirementFixPath(kind: RequirementKind): string {
  return FIX_PATHS[kind];
}

/** The bell's word for a requirement notice (the server's notices carry it as `metadata.kind`). */
export const SIGNIN_REQUIREMENT_NOTICE_KIND = 'signin_requirement';

/** The bell's word for the owners' and admins' digest of who still has a requirement to finish. */
export const SIGNIN_REQUIREMENT_DIGEST_KIND = 'signin_requirement_digest';

/** Team & Roles, where an owner or admin sees who still has it to do, reminds them and helps them sign in. */
export const HELP_THEM_TO = '/settings/team';

/** Where a notice's requirement (its `metadata.requirement_kind`) is fixed; null for anything that is not a kind. */
export function requirementFixPathOf(kind: unknown): string | null {
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(FIX_PATHS, kind) ? FIX_PATHS[kind as RequirementKind] : null;
}
